<template>
  <div>
    <j-abm
      ref="abm"
      name="office"
      avatar="environment"
      :suppliers="suppliers"
      :features="{
        search: false,
        create: false,
        update: false,
        delete: false,
      }">
      <template #filters>
        <div class="d-flex align-items-center" style="gap: 8px;">
          <a-button size="large" icon="filter" @click="openFilters">
          </a-button>
          <a-input-search
            v-model="filters.q"
            :placeholder="$t('search')"
            style="width: 280px"
            allow-clear
            size="large"
            @search="onSearchText"
            @change="onSearchText" />
        </div>
      </template>

      <template #toolbar>
        <a-button type="primary" size="large" icon="cloud-download" @click="exporting = true">
          {{ $t('export') }}
        </a-button>
      </template>

      <template #row="{ item }">
        <div>
          <div class="container">
            <div class="row">
              <div class="col-12 col-md-7">
                <h5>
                  <a-tooltip :title="$t(`enum.activation.${item.activation}`)" placement="left">
                    <a-badge :status="activation[item.activation] ? activation[item.activation].color : 'default'" />
                  </a-tooltip>
                  {{ item.name }}
                </h5>
                <div class="text-secondary">
                  {{ item.company }}
                </div>
                <div class="text-secondary">
                  {{ $t('externalCode') }}: {{ item.external_code }}
                </div>
              </div>
              <div class="col-12 col-md-5">
                <div class="text-secondary">
                  <a-icon type="environment" />
                  {{ item.address && item.address.street }}
                </div>
                <div class="text-secondary">
                  {{ $t('zip') }}: {{ item.address && item.address.zip }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #actions="{ item }">
        <j-actions :item="item" type="icon" @changed="onReload" />
      </template>
    </j-abm>

    <a-drawer
      :title="$t('filter')"
      placement="right"
      :visible="showFilters"
      :width="350"
      @close="onCloseFilters">
      <h3 slot="title">
        {{ $t('filter') }}
      </h3>

      <a-form-model :model="draftFilters">

        <a-form-model-item>
          <a-radio-group v-model="draftFilters.activation" size="small">
            <a-radio-button value="ALL">{{ $t('enum.activation.ALL') }}</a-radio-button>
            <a-radio-button value="ACTIVE">{{ $t('enum.activation.ACTIVE') }}</a-radio-button>
            <a-radio-button value="INACTIVE">{{ $t('enum.activation.INACTIVE') }}</a-radio-button>
          </a-radio-group>
        </a-form-model-item>

        <a-form-model-item>
          <a-select mode="multiple" v-model="draftFilters.services" :placeholder="$t('services')">
            <a-select-option v-for="service in services" :key="service" :value="service">
              {{ service }}
            </a-select-option>
          </a-select>
        </a-form-model-item>

        <a-form-model-item>
          <a-select mode="multiple" v-model="draftFilters.companies" :placeholder="$t('company')">
            <a-select-option v-for="company in companies" :key="company" :value="company">
              {{ company }}
            </a-select-option>
          </a-select>
        </a-form-model-item>

        <a-divider />

        <a-form-model-item>
          <a-button class="mr-2" type="primary" @click="onApplyFilters">
            {{ $t('apply') }}
          </a-button>
          <a-button type="link" @click="onClearFilters">
            {{ $t('clear') }}
          </a-button>
        </a-form-model-item>
      </a-form-model>
    </a-drawer>

    <j-export :show="exporting" :filters="normalizedFilters" @close="exporting = false" />
  </div>
</template>

<script>
import lms from '@/api/lms'
import acl from '@/auth/acl'
import { activation } from '@/utils/styles'
import JAbm from '@/views/shared/abm/search'
import JActions from './actions'
import JExport from './export'

const COMPANIES = ['OCA', 'RAPIPAGO', 'CORREO ARGENTINO']
const SERVICES = ['PICK_UP', 'DROP_OFF']
const DEFAULT_FILTERS = () => ({
  q: '',
  activation: 'ACTIVE',
  companies: [],
  services: ['PICK_UP'],
})

export default {
  components: {
    JAbm,
    JActions,
    JExport,
  },
  name: 'OfficeList',
  data: function () {
    return {
      acl,
      activation,
      companies: COMPANIES,
      services: SERVICES,
      filters: DEFAULT_FILTERS(),
      draftFilters: DEFAULT_FILTERS(),
      showFilters: false,
      exporting: false,
    }
  },
  computed: {
    suppliers() {
      return {
        fetchAll: page => lms.office.search(this.normalizedFilters, page),
      }
    },
    normalizedFilters() {
      const value = {
        q: String(this.filters.q || '').trim(),
      }

      if (this.filters.activation && this.filters.activation !== 'ALL') {
        value.activation = this.filters.activation
      }

      const selectedServices = this.filters.services || []
      if (selectedServices.length > 0) {
        value.services = selectedServices
      }

      const selectedCompanies = this.filters.companies || []
      if (selectedCompanies.length > 0) {
        value.companies = selectedCompanies
      }

      Object.keys(value).forEach(key => {
        if (value[key] === undefined || value[key] === null || value[key] === '') {
          delete value[key]
        }
      })

      return value
    },
  },
  methods: {
    openFilters() {
      this.draftFilters = {
        ...this.filters,
        companies: [...this.filters.companies],
        services: [...this.filters.services],
      }
      this.showFilters = true
    },
    onCloseFilters() {
      this.showFilters = false
    },
    onApplyFilters() {
      this.filters = {
        q: this.filters.q,
        ...this.draftFilters,
        companies: [...this.draftFilters.companies],
        services: [...this.draftFilters.services],
      }
      this.showFilters = false
      this.$store.commit('results/SET_PAGE', { key: 'office', body: { page: 1, size: 30 } })
      this.onReload()
    },
    onClearFilters() {
      const currentQuery = this.filters.q
      this.draftFilters = { ...DEFAULT_FILTERS(), q: currentQuery }
      this.filters = { ...DEFAULT_FILTERS(), q: currentQuery }
      this.showFilters = false
      this.$store.commit('results/SET_PAGE', { key: 'office', body: { page: 1, size: 30 } })
      this.onReload()
    },
    onSearchText() {
      this.$store.commit('results/SET_PAGE', { key: 'office', body: { page: 1, size: 30 } })
      this.onReload()
    },
    onReload() {
      this.$refs.abm.fetch()
    },
  },
}
</script>
